import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardActionArea,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  Factory as FactoryIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { useFeedDashboard } from '../hooks/useFeed';
import type { FeedStockAlert, FeedBatchAlert } from '../types';

const navCards = [
  {
    title: 'Stock Levels',
    description: 'Monitor current inventory levels, set thresholds, and procure new feed stock.',
    icon: InventoryIcon,
    path: '/feed/stock',
    color: '#42a5f5',
    accent: 'rgba(66,165,245,0.2)',
    border: 'rgba(66,165,245,0.3)',
  },
  {
    title: 'Procurements',
    description: 'Track feed procurement orders, mark deliveries, and manage supplier orders.',
    icon: ShippingIcon,
    path: '/feed/procurements',
    color: '#ab47bc',
    accent: 'rgba(171,71,188,0.2)',
    border: 'rgba(171,71,188,0.3)',
  },
  {
    title: 'Batch Assignments',
    description: 'Assign feed to bird batches, track consumption, and manage allocations.',
    icon: AssignmentIcon,
    path: '/feed/assignments',
    color: '#66bb6a',
    accent: 'rgba(102,187,106,0.2)',
    border: 'rgba(102,187,106,0.3)',
  },
  {
    title: 'Raw Materials',
    description: 'Procure raw materials for feed production and monitor stock levels.',
    icon: InventoryIcon,
    path: '/feed/raw-materials',
    color: '#ff9800',
    accent: 'rgba(255,152,0,0.2)',
    border: 'rgba(255,152,0,0.3)',
  },
  {
    title: 'Feed Production',
    description: 'Produce feed from raw materials and add to stock inventory.',
    icon: FactoryIcon,
    path: '/feed/production',
    color: '#26c6da',
    accent: 'rgba(38,198,218,0.2)',
    border: 'rgba(38,198,218,0.3)',
  },
  {
    title: 'Calculator',
    description: 'Estimate daily and weekly feed requirements based on bird count.',
    icon: CalculateIcon,
    path: '/feed/calculator',
    color: '#ef5350',
    accent: 'rgba(239,83,80,0.2)',
    border: 'rgba(239,83,80,0.3)',
  },
];

const statCards = [
  {
    label: 'Weekly Consumption',
    valueKey: 'weekly_consumption_kg',
    suffix: ' kg',
    icon: InventoryIcon,
    color: '#42a5f5',
    accent: 'rgba(66,165,245,0.3)',
  },
  {
    label: 'Stock Value',
    valueKey: 'total_stock_value',
    prefix: 'GH₵ ',
    icon: MoneyIcon,
    color: '#66bb6a',
    accent: 'rgba(102,187,106,0.3)',
  },
  {
    label: 'Low Stock Alerts',
    valueKey: 'low_stock_alerts',
    icon: WarningIcon,
    color: '#ff9800',
    accent: 'rgba(255,152,0,0.3)',
  },
  {
    label: 'Active Batches',
    valueKey: 'active_batches',
    icon: AssignmentIcon,
    color: '#ab47bc',
    accent: 'rgba(171,71,188,0.3)',
  },
];

const FeedManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: dashboard, isLoading: dashboardLoading, isFetching } = useFeedDashboard();

  if (dashboardLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress sx={{ color: '#ff9800' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ color: '#ffffff', letterSpacing: '-0.02em' }}
        >
          Feed Management
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
          Monitor stock, procurements, batch assignments and production.
        </Typography>
      </Box>

      {/* Stats Cards */}
      {dashboard && (
        <Grid container spacing={2.5} mb={4}>
          {statCards.map(
            ({ label, valueKey, prefix = '', suffix = '', icon: Icon, color, accent }) => (
              <Grid key={label} size={{ xs: 12, md: 3 }}>
                <Paper
                  sx={{
                    p: 2.5,
                    background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
                    border: `1px solid ${accent}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: color,
                    },
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          color: 'rgba(255,255,255,0.5)',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color, mt: 0.5 }}>
                        {prefix}
                        {parseFloat(String(dashboard.overview[valueKey] || '0')).toLocaleString()}
                        {suffix}
                      </Typography>
                    </Box>
                    <Icon sx={{ fontSize: 36, color, opacity: 0.3 }} />
                  </Box>
                </Paper>
              </Grid>
            )
          )}
        </Grid>
      )}

      {/* Stock Alerts */}
      {dashboard?.stock_alerts && dashboard.stock_alerts.length > 0 && (
        <Accordion
          defaultExpanded
          sx={{
            mb: 2,
            background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
            border: '1px solid rgba(255,152,0,0.3)',
            borderRadius: '12px !important',
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#ff9800' }} />}
            sx={{
              borderRadius: 2,
              '&:hover': { background: 'rgba(255,152,0,0.05)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: '#ffffff' }}>
                Stock Alerts
              </Typography>
              <Chip
                label={dashboard.stock_alerts.length}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,152,0,0.2)',
                  color: '#ff9800',
                  fontWeight: 700,
                  height: 20,
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, maxHeight: 300, overflowY: 'auto' }}>
            {dashboard.stock_alerts.map((alert: FeedStockAlert, index: number) => (
              <Alert
                key={index}
                severity="warning"
                sx={{
                  mb: 1,
                  background: 'rgba(255,152,0,0.08)',
                  border: '1px solid rgba(255,152,0,0.2)',
                  color: 'rgba(255,255,255,0.85)',
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: '#ff9800' },
                }}
              >
                <strong>
                  {alert.feed_type} - {alert.feed_brand}:
                </strong>{' '}
                {parseFloat(alert.current_stock_kg).toFixed(2)} kg remaining (
                {alert.stock_level_percentage.toFixed(1)}% of threshold) —{' '}
                {alert.days_of_stock_remaining.toFixed(1)} days left
              </Alert>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Batch Alerts */}
      {dashboard?.batch_alerts && dashboard.batch_alerts.length > 0 && (
        <Accordion
          defaultExpanded={false}
          sx={{
            mb: 4,
            background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
            border: '1px solid rgba(239,83,80,0.3)',
            borderRadius: '12px !important',
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#ef5350' }} />}
            sx={{ '&:hover': { background: 'rgba(239,83,80,0.05)' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AssignmentIcon sx={{ color: '#ef5350', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: '#ffffff' }}>
                Batch Feed Alerts
              </Typography>
              <Chip
                label={dashboard.batch_alerts.length}
                size="small"
                sx={{
                  bgcolor: 'rgba(239,83,80,0.2)',
                  color: '#ef5350',
                  fontWeight: 700,
                  height: 20,
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, maxHeight: 300, overflowY: 'auto' }}>
            {dashboard.batch_alerts.map((alert: FeedBatchAlert, index: number) => (
              <Alert
                key={index}
                severity={alert.remaining_stock_kg === 0 ? 'error' : 'warning'}
                sx={{
                  mb: 1,
                  background:
                    alert.remaining_stock_kg === 0
                      ? 'rgba(239,83,80,0.08)'
                      : 'rgba(255,152,0,0.08)',
                  border:
                    alert.remaining_stock_kg === 0
                      ? '1px solid rgba(239,83,80,0.2)'
                      : '1px solid rgba(255,152,0,0.2)',
                  color: 'rgba(255,255,255,0.85)',
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: alert.remaining_stock_kg === 0 ? '#ef5350' : '#ff9800',
                  },
                }}
              >
                <strong>{alert.batch_name}:</strong> {alert.feed_type} - {alert.feed_brand} has{' '}
                {alert.remaining_stock_kg} kg remaining ({alert.consumption_percentage.toFixed(1)}%
                consumed)
              </Alert>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Navigation Cards */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, color: '#ffffff' }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3}>
        {navCards.map(({ title, description, icon: Icon, path, color, accent, border }) => (
          <Grid key={title} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
                border: `1px solid ${border}`,
                borderRadius: 3,
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0px 8px 24px ${accent}`,
                  borderColor: color,
                },
              }}
            >
              <CardActionArea onClick={() => navigate(path)} sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      bgcolor: accent,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff' }}>
                    {title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, lineHeight: 1.6 }}
                >
                  {description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
                  <Typography variant="body2" fontWeight={600}>
                    View Details
                  </Typography>
                  <ArrowForwardIcon fontSize="small" />
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default FeedManagementPage;
